import classNames from "classnames";

import ListItem from "@/components/ListItem";
import Tag from "@/components/Tag";
import { formatDate } from "@/pages/_layouts/jobs/_utils";
import {
  formatCatalogValues,
  getCareerIntentSummaryRows,
  NO_DESIRED_CHANGES_CONSULTANT_LABEL
} from "./careerIntentCatalog";
import { getNextRoleLabel, type CareerPlanGoal } from "./careerPlanHistory";

type CareerGoalsDetailProps = {
  goal: CareerPlanGoal;
};

export function CareerGoalsDetail({ goal }: CareerGoalsDetailProps) {
  const nextRole = getNextRoleLabel(goal);
  const targetPositionLabels = goal.hasNoDesiredChanges
    ? []
    : formatCatalogValues("targetPosition", goal.targetPositions);
  const summaryRows = getCareerIntentSummaryRows(goal, true).filter(
    (row) => !(targetPositionLabels.length > 0 && row.label === "Target Positions")
  );
  const heading = goal.hasNoDesiredChanges ? NO_DESIRED_CHANGES_CONSULTANT_LABEL : nextRole || "Career Goals";

  return (
    <div className="flex min-w-0 w-full flex-col" data-testid={`career-goals-detail-${goal.id}`}>
      {targetPositionLabels.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-2" data-testid={`career-goals-target-positions-${goal.id}`}>
          {targetPositionLabels.map((label, index) => (
            <li key={`${label}-${index}`}>
              <Tag tagType="secondary" size="large">
                {label}
              </Tag>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-2 text-title-large-bold font-serif text-sys-brand-on-surface">{heading}</p>
      )}

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <ListItem iconName="event" overline="Created" headline={formatDate(goal.createdAt) || "—"} iconBackground />
        <ListItem
          iconName="history"
          overline="Last Updated"
          headline={formatDate(goal.updatedAt) || "—"}
          iconBackground
        />
        {goal.archivedAt ? (
          <ListItem iconName="archive" overline="Archived on" headline={formatDate(goal.archivedAt)} iconBackground />
        ) : null}
        {summaryRows.map((row) => {
          const isNotes = row.label === "Additional Context";
          return (
            <ListItem
              key={row.label}
              className={classNames(isNotes && "md:col-span-2")}
              iconName="list"
              overline={row.label}
              headline={row.values.join(", ")}
              longContent
              iconBackground
            />
          );
        })}
      </div>
    </div>
  );
}
